/**
 * Geode Voice (UPT-2000 lots V0 et V1a) — client de l'API vocale du socle
 * (en-têtes SaaS) :
 *   - POST /api/v1/ai/voice/session : session éphémère. Codes d'erreur typés :
 *     403 voice.addon_inactive, 503 voice.not_configured, 502 voice.upstream_error
 *     et, depuis V1a, 429 voice.budget_exhausted (`resetAt`) et 429
 *     voice.rate_limited (`retryAfterSeconds`) ;
 *   - POST /api/v1/ai/voice/session/{sessionId}/usage : rapport d'usage ;
 *   - GET /api/v1/ai/voice/usage?month= : consommation du mois ;
 *   - POST /api/v1/ai/voice/intents : journal des intentions (V1b, sans audio ni transcription).
 * Le serveur fait foi (`rest/SPEC-UPT-2000-V1.md` §4).
 */
import BaseApi from '@/uptk/core/api/BaseApi';
import { getPreferredAccessToken } from '@/uptk/core/api/clerkTokenProvider';
import { buildSaasHeaders, resolveSaasContext } from '@/uptk/core/api/saasContext';
import type {
  VoiceContext,
  VoiceIntentBody,
  VoiceLimits,
  VoiceSessionInfo,
  VoiceSource,
  VoiceToolDefinition,
  VoiceUsage,
  VoiceUsageAck,
  VoiceUsageBody,
} from './voiceTypes';

export type VoiceApiErrorCode =
  | 'voice.addon_inactive'
  | 'voice.not_configured'
  | 'voice.upstream_error'
  | 'voice.budget_exhausted'
  | 'voice.rate_limited'
  // Relecture rest du 18.10 (R2) : 400, rapport d'usage refusé avant toute écriture (`detail: "tokens"` = jetons au-delà du plafond
  // `2 000 + débit × secondes écoulées`, ou incrément au-delà de la borne absolue).
  | 'voice.usage_invalid'
  | 'voice.http_error';

/** Champs additionnels des erreurs 429 (spec rest V1 §4.3). */
export type VoiceApiErrorExtras = {
  /** `voice.budget_exhausted` : début du mois suivant (ISO, UTC). */
  resetAt?: string;
  /** `voice.rate_limited` : attente conseillée, en secondes. */
  retryAfterSeconds?: number;
  /** `voice.usage_invalid` : ce qui est refusé (`tokens`…). */
  detail?: string;
};

export class VoiceApiError extends Error {
  readonly code: VoiceApiErrorCode;
  readonly status: number;
  readonly resetAt?: string;
  readonly retryAfterSeconds?: number;
  readonly detail?: string;

  constructor(code: VoiceApiErrorCode, status: number, message: string, extras: VoiceApiErrorExtras = {}) {
    super(message);
    this.name = 'VoiceApiError';
    this.code = code;
    this.status = status;
    this.resetAt = extras.resetAt;
    this.retryAfterSeconds = extras.retryAfterSeconds;
    this.detail = extras.detail;
  }
}

const ERROR_CODES: Record<number, VoiceApiErrorCode> = {
  403: 'voice.addon_inactive',
  429: 'voice.rate_limited',
  503: 'voice.not_configured',
  502: 'voice.upstream_error',
};

const KNOWN_ERROR_CODES: ReadonlySet<string> = new Set<VoiceApiErrorCode>([
  'voice.addon_inactive',
  'voice.not_configured',
  'voice.upstream_error',
  'voice.budget_exhausted',
  'voice.rate_limited',
  'voice.usage_invalid',
]);

/** Limites par défaut quand le serveur n'en renvoie pas (spec web V1 §4) : 600 / 120 / 30 / null / 0. */
export const DEFAULT_VOICE_LIMITS: VoiceLimits = {
  maxSessionSeconds: 600,
  idleStopSeconds: 120,
  usageReportSeconds: 30,
  monthlySecondsMax: null,
  monthlySecondsUsed: 0,
};

const positiveInteger = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : fallback;

const nonNegativeInteger = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.floor(value) : fallback;

const normalizeTool = (value: unknown): VoiceToolDefinition => {
  const candidate = (value ?? {}) as Partial<VoiceToolDefinition>;
  return {
    type: 'function',
    name: typeof candidate.name === 'string' ? candidate.name : '',
    description: typeof candidate.description === 'string' ? candidate.description : '',
    parameters:
      candidate.parameters && typeof candidate.parameters === 'object'
        ? (candidate.parameters as Record<string, unknown>)
        : {},
  };
};

const normalizeLimits = (value: unknown): VoiceLimits => {
  const candidate = (value && typeof value === 'object' ? value : {}) as Partial<
    Record<keyof VoiceLimits, unknown>
  >;
  return {
    maxSessionSeconds: positiveInteger(candidate.maxSessionSeconds, DEFAULT_VOICE_LIMITS.maxSessionSeconds),
    idleStopSeconds: positiveInteger(candidate.idleStopSeconds, DEFAULT_VOICE_LIMITS.idleStopSeconds),
    usageReportSeconds: positiveInteger(candidate.usageReportSeconds, DEFAULT_VOICE_LIMITS.usageReportSeconds),
    monthlySecondsMax:
      typeof candidate.monthlySecondsMax === 'number' &&
      Number.isFinite(candidate.monthlySecondsMax) &&
      candidate.monthlySecondsMax > 0
        ? Math.floor(candidate.monthlySecondsMax)
        : null,
    monthlySecondsUsed: nonNegativeInteger(candidate.monthlySecondsUsed, DEFAULT_VOICE_LIMITS.monthlySecondsUsed),
  };
};

const normalizeSource = (value: unknown): VoiceSource => (value === 'tenant' ? 'tenant' : 'geode');

const normalizeSession = (value: unknown): VoiceSessionInfo => {
  const candidate = (value ?? {}) as Partial<VoiceSessionInfo>;
  return {
    sessionId: typeof candidate.sessionId === 'string' ? candidate.sessionId : '',
    ephemeralKey: typeof candidate.ephemeralKey === 'string' ? candidate.ephemeralKey : '',
    expiresAt: typeof candidate.expiresAt === 'string' ? candidate.expiresAt : '',
    webrtcUrl: typeof candidate.webrtcUrl === 'string' ? candidate.webrtcUrl : '',
    model: typeof candidate.model === 'string' ? candidate.model : '',
    voice: typeof candidate.voice === 'string' ? candidate.voice : '',
    instructions: typeof candidate.instructions === 'string' ? candidate.instructions : '',
    tools: Array.isArray(candidate.tools) ? candidate.tools.map(normalizeTool) : [],
    // V1a : les champs inconnus ne sont plus jetés (`source` et `limits` pilotent la session).
    source: normalizeSource(candidate.source),
    limits: normalizeLimits(candidate.limits),
  };
};

const normalizeUsageAck = (value: unknown): VoiceUsageAck => {
  const candidate = (value ?? {}) as Partial<Record<keyof VoiceUsageAck, unknown>>;
  // Seule une réponse explicite `continue: false` arrête la session (spec web V1 §6 n° 6).
  const reason =
    candidate.reason === 'budget' || candidate.reason === 'max_duration' ? candidate.reason : null;
  return {
    continue: candidate.continue !== false,
    reason,
    monthlySecondsUsed: nonNegativeInteger(candidate.monthlySecondsUsed, 0),
  };
};

const normalizeVoiceUsage = (value: unknown): VoiceUsage => {
  const candidate = (value ?? {}) as Record<string, unknown>;
  const seconds = (candidate.seconds && typeof candidate.seconds === 'object' ? candidate.seconds : {}) as Record<
    string,
    unknown
  >;
  const tokens = (candidate.tokens && typeof candidate.tokens === 'object' ? candidate.tokens : {}) as Record<
    string,
    unknown
  >;
  const rawCost = candidate.cost && typeof candidate.cost === 'object' ? (candidate.cost as Record<string, unknown>) : null;
  const cost =
    rawCost && typeof rawCost.amount === 'number' && Number.isFinite(rawCost.amount) && typeof rawCost.currency === 'string'
      ? {
          amount: rawCost.amount,
          currency: rawCost.currency,
          estimated: rawCost.estimated !== false,
          complete: rawCost.complete !== false,
        }
      : null;
  return {
    period: typeof candidate.period === 'string' ? candidate.period : '',
    configured: candidate.configured === true,
    source: candidate.source === 'geode' || candidate.source === 'tenant' ? candidate.source : null,
    seconds: {
      used: nonNegativeInteger(seconds.used, 0),
      max:
        typeof seconds.max === 'number' && Number.isFinite(seconds.max) && seconds.max > 0
          ? Math.floor(seconds.max)
          : null,
    },
    tokens: {
      inputText: nonNegativeInteger(tokens.inputText, 0),
      inputAudio: nonNegativeInteger(tokens.inputAudio, 0),
      outputText: nonNegativeInteger(tokens.outputText, 0),
      outputAudio: nonNegativeInteger(tokens.outputAudio, 0),
    },
    cost,
    resetAt: typeof candidate.resetAt === 'string' ? candidate.resetAt : '',
  };
};

/** Frappe une session vocale éphémère (la clé reste côté serveur). */
export const createVoiceSession = async (
  locale: string,
  context: VoiceContext
): Promise<VoiceSessionInfo> => {
  try {
    const response = await BaseApi.post('/api/v1/ai/voice/session', { locale, context });
    return normalizeSession(response.data);
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status ?? 0;
    const body = (
      error as {
        response?: {
          data?: { code?: string; message?: string; resetAt?: unknown; retryAfterSeconds?: unknown; detail?: unknown };
        };
      }
    )?.response?.data;
    const code: VoiceApiErrorCode =
      typeof body?.code === 'string' && KNOWN_ERROR_CODES.has(body.code)
        ? (body.code as VoiceApiErrorCode)
        : (ERROR_CODES[status] ?? 'voice.http_error');
    throw new VoiceApiError(code, status, body?.message ?? 'voice api error', {
      detail: typeof body?.detail === 'string' ? body.detail : undefined,
      resetAt: typeof body?.resetAt === 'string' ? body.resetAt : undefined,
      retryAfterSeconds:
        typeof body?.retryAfterSeconds === 'number' && Number.isFinite(body.retryAfterSeconds)
          ? body.retryAfterSeconds
          : undefined,
    });
  }
};

export const isVoiceApiError = (error: unknown): error is VoiceApiError =>
  error instanceof VoiceApiError;

const usagePath = (sessionId: string): string =>
  `/api/v1/ai/voice/session/${encodeURIComponent(sessionId)}/usage`;

/**
 * Rapport d'usage d'une session (incréments + durée cumulée). Le même `seq` et le même corps peuvent être
 * renvoyés après un échec : le serveur rend alors la réponse mémorisée sans rien ajouter (rejeu idempotent).
 */
export const reportVoiceUsage = async (
  sessionId: string,
  body: VoiceUsageBody
): Promise<VoiceUsageAck> => {
  const response = await BaseApi.post(usagePath(sessionId), body);
  return normalizeUsageAck(response.data);
};

/** Consommation du mois (`month` = `YYYY-MM`, défaut : mois courant UTC côté serveur). */
export const getVoiceUsage = async (month?: string): Promise<VoiceUsage> => {
  const response = await BaseApi.get('/api/v1/ai/voice/usage', month ? { month } : undefined);
  return normalizeVoiceUsage(response.data);
};

/**
 * Journal des intentions : `op`, codes valides, canal et issue seulement — jamais de libellé, d'audio ni de
 * transcription. Un échec est ignoré (le journal ne bloque jamais le geste de l'utilisateur).
 */
export const postVoiceIntent = async (body: VoiceIntentBody): Promise<void> => {
  try {
    await BaseApi.post('/api/v1/ai/voice/intents', body);
  } catch {
    // journal facultatif
  }
};

/**
 * En-têtes des appels `fetch` de la voix : mêmes en-têtes SaaS et même jeton que le flux du copilote
 * (extrait de `askCopilot` en V1a). `accept` vaut `text/event-stream` pour le flux du copilote ;
 * les rapports d'usage passent `application/json`.
 */
export const voiceFetchHeaders = async (accept = 'text/event-stream'): Promise<Headers> => {
  const token = await getPreferredAccessToken();
  const headers = new Headers();
  Object.entries(buildSaasHeaders(resolveSaasContext({}))).forEach(([name, value]) => {
    headers.set(name.toLowerCase(), value);
  });
  headers.set('Content-Type', 'application/json');
  headers.set('Accept', accept);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
};

/**
 * Dernier rapport (`final: true`) à la fermeture de l'onglet : `fetch` avec `keepalive` (survit au déchargement
 * de la page). Les en-têtes sont préparés AVANT (le jeton se lit de façon asynchrone, ce qui n'est pas possible
 * pendant `pagehide`) ; l'échec est ignoré.
 */
export const sendVoiceUsageKeepalive = (
  sessionId: string,
  body: VoiceUsageBody,
  headers: Headers
): void => {
  try {
    void fetch(usagePath(sessionId), {
      method: 'POST',
      headers,
      keepalive: true,
      body: JSON.stringify(body),
    }).catch(() => undefined);
  } catch {
    // fermeture d'onglet : rien à faire de plus
  }
};
